import { redirectDocument } from 'react-router';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { crewClaimContext } from '~/app/features/crew/crewClaimMiddleware';
import { crewOrderContext } from '~/app/features/order-middleware/orderMiddleware';
import { settingsContext } from '~/app/settingsMiddleware';
import { stateSchema } from '~/stores/app';
import api from '~/utils/api';
import {
  getParentLocation,
  isAppEmbedded,
  navigateParent,
} from '~/utils/appEmbed';
import { isCreditClaim } from '~/utils/claimLineItem';
import { computeCost, sumBy } from '~/utils/compute';
import { CurrencyCode } from '~/utils/constants';
import { makeAction, makeLoader, redirect } from '~/utils/routing';
import { crewOrderSchema } from '~/utils/schemas';

const computeShopNowIncentive = (
  cli: NonNullable<
    z.infer<typeof stateSchema.shape.crewClaim>
  >['claimLineItems'],
  incentive: {
    type: 'Percent' | 'Fixed' | 'None';
    amount: number;
  } = { type: 'None', amount: 0 },
) => {
  const lineItemTotal = sumBy(computeCost)(cli.filter(isCreditClaim));

  return match(incentive)
    .with({ type: 'Percent' }, ({ amount }) => ({
      lineItemTotal,
      amount: lineItemTotal * amount,
    }))
    .with({ type: 'Fixed' }, ({ amount }) => ({
      lineItemTotal,
      amount,
    }))
    .with({ type: 'None' }, () => ({ lineItemTotal, amount: 0 }))
    .exhaustive();
};

export const stateValidationSchema = stateSchema
  .pick({
    order: true,
    crewClaim: true,
    settings: true,
  })
  .extend({
    settings: stateSchema.shape.settings.unwrap(),
    order: crewOrderSchema,
    crewClaim: stateSchema.shape.crewClaim
      .unwrap()
      .refine((claim) => claim.claimType === 'Return'),
  });

export const { loader, useLoaderData } = makeLoader(({ context }) => {
  const crewClaim = context.get(crewClaimContext);

  const hasCreditClaims = crewClaim.claimLineItems.some(isCreditClaim);
  if (!crewClaim.offerShopNow || !hasCreditClaims) {
    return redirect('../address');
  }

  const incentive = computeShopNowIncentive(
    crewClaim.claimLineItems,
    crewClaim.offerShopNow.shopNowBenefits.find(
      (benefit) => benefit.kind === 'incentive',
    )?.incentive,
  );

  return {
    credit: incentive.lineItemTotal,
    incentive: incentive.amount,
    benefits: crewClaim.offerShopNow.shopNowBenefits,
  };
});

const handleRedirect = (url: string) => {
  if (isAppEmbedded()) {
    navigateParent(url);

    return {
      ok: true,
      message: 'Redirecting parent',
    };
  }

  return redirectDocument(url);
};

export const { action, useActionResult } = makeAction(
  async ({ params, request, context }, appStore) => {
    const settings = context.get(settingsContext);
    const order = context.get(crewOrderContext);
    const crewClaim = context.get(crewClaimContext);

    if (!crewClaim.offerShopNow) {
      throw new Error('Shop Now is not available for this order');
    }

    // remove any previous shop now results to start a new session
    appStore.dispatch(appStore.actions.crewClaim.clearShopNowResults());

    const returnUrl = isAppEmbedded()
      ? await getParentLocation()
      : new URL(`${params.store}`, new URL(request.url)).href;

    const incentive = computeShopNowIncentive(
      crewClaim.claimLineItems,
      crewClaim.offerShopNow.shopNowBenefits.find(
        (benefit) => benefit.kind === 'incentive',
      )?.incentive,
    );

    appStore.dispatch(
      appStore.actions.crewClaim.setShopNowIncentiveAmountApplied(
        incentive.amount,
      ),
    );

    return api
      .createShopNowSession({
        params: { storefrontId: settings.storefrontId },
        body: {
          orderIdFromPlatform: order.idFromPlatform,
          shopNowEvent: crewClaim.offerShopNow,
          customerAppHostConfig: isAppEmbedded()
            ? {
                type: 'embedded',
                parentHostUrl: returnUrl,
                orderIdFromPlatform: order.idFromPlatform,
              }
            : {
                type: 'standalone',
                slug: settings.storeSlug,
                orderIdFromPlatform: order.idFromPlatform,
              },

          returnCredit: {
            amount: incentive.lineItemTotal,
            currencyCode: order.currencyCode as CurrencyCode, // TODO fix currency code without casting
          },
          returnCreditIncentive: {
            amount: incentive.amount,
            currencyCode: order.currencyCode as CurrencyCode, // TODO fix currency code without casting
          },
        },
      })
      .then(({ startShoppingIdUrl, id }) => {
        appStore.dispatch(appStore.actions.crewClaim.setShopNowId(id));

        return handleRedirect(startShoppingIdUrl);
      });
  },
);
